import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TablePagination,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Modal,
  Backdrop,
  Fade,
  Tabs,
  Tab,
  Chip,
  Alert,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  Description as WordIcon,
  TableChart as ExcelIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  Archive as ArchiveIcon,
  Code as CodeIcon,
  TextSnippet as TextIcon,
  InsertDriveFile as FileIcon,
  Link as LinkIcon,
  Assignment as TaskIcon,
  Person as LeadIcon,
  Folder as FolderIcon,
  DeleteOutline as TrashIcon,
  RestoreFromTrash as RestoreIcon,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { FileMetadataDTO } from '../api/generated/requests/types.gen';
import { MinIoFileStorageService } from '../api/generated/requests/services.gen';
import { useFileQuery } from '../hooks/files/useFileQuery';
import { useFileMutations } from '../hooks/files/useFileMutations';
import DeleteConfirmDialog from "../components/Common/DeleteConfirmDialog";
import LazyThumbnail from "../components/Common/LazyThumbnail";
import TaskDetailDialog from "../components/Tasks/TaskDetailDialog";
import LeadDetailDialog from "../components/Leads/LeadDetailDialog";
import ColumnConfigDialog, { ColumnConfig } from '../components/Common/ColumnConfigDialog';
// import { AvatarCacheService } from '../services/avatarCacheService';
import { useUserData } from '../hooks/useUserData';
// import { Avatar } from '@mui/material';

// Component to display file type icons based on content type
const FileTypeIcon: React.FC<{ contentType?: string; size?: 'small' | 'medium' | 'large' }> = ({ 
  contentType, 
  size = 'small' 
}) => {
  const getFileIcon = (mimeType?: string) => {
    if (!mimeType) return <FileIcon fontSize={size} />;

    const type = mimeType.toLowerCase();

    // PDF files
    if (type.includes('pdf')) {
      return <PdfIcon fontSize={size} sx={{ color: '#d32f2f' }} />;
    }

    // Word documents
    if (type.includes('word') || type.includes('msword') || 
        type.includes('officedocument.wordprocessingml')) {
      return <WordIcon fontSize={size} sx={{ color: '#1976d2' }} />;
    }

    // Excel files
    if (type.includes('excel') || type.includes('spreadsheet') || 
        type.includes('officedocument.spreadsheetml')) {
      return <ExcelIcon fontSize={size} sx={{ color: '#388e3c' }} />;
    }

    // PowerPoint files
    if (type.includes('powerpoint') || type.includes('presentation') ||
        type.includes('officedocument.presentationml')) {
      return <WordIcon fontSize={size} sx={{ color: '#f57c00' }} />;
    }

    // Images
    if (type.startsWith('image/')) {
      return <ImageIcon fontSize={size} sx={{ color: '#7b1fa2' }} />;
    }

    // Videos
    if (type.startsWith('video/')) {
      return <VideoIcon fontSize={size} sx={{ color: '#c2185b' }} />;
    }

    // Audio files
    if (type.startsWith('audio/')) {
      return <AudioIcon fontSize={size} sx={{ color: '#00796b' }} />;
    }

    // Archive files
    if (type.includes('zip') || type.includes('rar') || type.includes('7z') || 
        type.includes('tar') || type.includes('gzip')) {
      return <ArchiveIcon fontSize={size} sx={{ color: '#5d4037' }} />;
    }

    // Code files
    if (type.includes('javascript') || type.includes('json') || type.includes('xml') ||
        type.includes('html') || type.includes('css') || type.includes('text/plain')) {
      return <CodeIcon fontSize={size} sx={{ color: '#455a64' }} />;
    }

    // Text files
    if (type.startsWith('text/')) {
      return <TextIcon fontSize={size} sx={{ color: '#616161' }} />;
    }

    // Default file icon
    return <FileIcon fontSize={size} sx={{ color: '#757575' }} />;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {getFileIcon(contentType)}
    </Box>
  );
};

type FileColumn = {
  id: keyof FileMetadataDTO | 'actions' | 'thumbnail' | 'select' | 'userId';
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
};

const defaultColumns: FileColumn[] = [
  { id: 'select', label: 'zaznacz', minWidth: 50, align: 'center' },
  { id: 'thumbnail', label: 'Miniatura', minWidth: 60, align: 'center' },
  { id: 'originalFilename', label: 'Nazwa pliku', minWidth: 150 },
  { id: 'contentType', label: 'Typ', minWidth: 80, align: 'center' },
  { id: 'fileSize', label: 'Rozmiar', minWidth: 80 },
  { id: 'userId', label: 'Dodane przez', minWidth: 60, align: 'center' },
  { id: 'createdAt', label: 'Data dodania', minWidth: 120 },
  { id: 'actions', label: 'Akcje', align: 'right', minWidth: 160 }
];

const FilesPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<FileMetadataDTO | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sortBy, setSortBy] = useState<keyof FileMetadataDTO>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string>('');
  const [confirmPreviewOpen, setConfirmPreviewOpen] = useState(false);
  const [fileToPreview, setFileToPreview] = useState<FileMetadataDTO | null>(null);
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const [leadDetailOpen, setLeadDetailOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionsAnchorEl, setBulkActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [columns, setColumns] = useState<FileColumn[]>(defaultColumns);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();

  // Avatar cache state
  // const [cachedAvatars, setCachedAvatars] = useState<Map<string, { avatarUrl: string | null; timestamp: number }>>(new Map());
  
  // User data for display names
  const { domainUsers } = useUserData();

  // Helper function to get user display name
  const getUserDisplayName = (userId?: string): string => {
    if (!userId) return 'Nieznany użytkownik';
    
    const user = domainUsers?.find(u => u.id === userId);
    if (user) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.login || userId;
    }
    return userId;
  };

  // Helper function to get user initials
  const getUserInitials = (userId?: string): string => {
    if (!userId) return '?';
    
    const user = domainUsers?.find(u => u.id === userId);
    if (user) {
      const first = user.firstName?.charAt(0)?.toUpperCase() || '';
      const last = user.lastName?.charAt(0)?.toUpperCase() || '';
      return first + last || user.login?.charAt(0)?.toUpperCase() || '?';
    }
    return userId.charAt(0).toUpperCase();
  };

  const { files, isLoading, refreshFiles } = useFileQuery();
  const { 
    uploadFile, 
    deleteFile, 
    restoreFile, 
    downloadFile, 
    bulkStatusChange, 
    bulkDelete,
    isUploading, 
    isDeleting, 
    isChangingStatus,
    isBulkChangingStatus,
    isBulkDeleting
  } = useFileMutations();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0); // Reset page when switching tabs
    // Clear selection when switching tabs
    setSelectedFiles(new Set());
    setSelectAll(false);
  };

  // Handle tab switching from URL parameters (from search navigation)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam, 10);
      if (tabIndex >= 0 && tabIndex <= 1) { // Only allow valid tab indices
        setTabValue(tabIndex);
      }
    }
  }, [location.search]);

  // Preload avatars when files are loaded
  // useEffect(() => {
  //   if (files && files.length > 0) {
  //     const userIds = files
  //       .map(file => file.userId)
  //       .filter((userId): userId is string => Boolean(userId));
      
  //     if (userIds.length > 0) {
  //       AvatarCacheService.preloadAvatars(userIds).then(() => {
  //         // Update local cache state after preloading
  //         const newCachedAvatars = new Map(cachedAvatars);
  //         userIds.forEach(userId => {
  //           const cached = AvatarCacheService.getCachedAvatar(userId);
  //           if (cached) {
  //             newCachedAvatars.set(userId, {
  //               avatarUrl: cached.avatarUrl,
  //               timestamp: cached.timestamp
  //             });
  //           }
  //         });
  //         setCachedAvatars(newCachedAvatars);
  //       });
  //     }
  //   }
  // }, [files]);

  // Selection handlers
  const handleSelectFile = (filename: string) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(filename)) {
      newSelected.delete(filename);
    } else {
      newSelected.add(filename);
    }
    setSelectedFiles(newSelected);

    // Update select all state
    const currentPageFiles = sortedFiles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const allCurrentPageSelected = currentPageFiles.every(file => newSelected.has(file.filename || ''));
    setSelectAll(allCurrentPageSelected && currentPageFiles.length > 0);
  };

  const handleSelectAll = () => {
    const currentPageFiles = sortedFiles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const newSelected = new Set(selectedFiles);

    if (selectAll) {
      // Deselect all files on current page
      currentPageFiles.forEach(file => {
        if (file.filename) {
          newSelected.delete(file.filename);
        }
      });
    } else {
      // Select all files on current page
      currentPageFiles.forEach(file => {
        if (file.filename) {
          newSelected.add(file.filename);
        }
      });
    }

    setSelectedFiles(newSelected);
    setSelectAll(!selectAll);
  };

  const handleBulkMoveToTrash = () => {
    const filenames = Array.from(selectedFiles);
    bulkStatusChange(filenames, 'DETACHED');
    setSelectedFiles(new Set());
    setSelectAll(false);
    setBulkActionsAnchorEl(null);
  };

  const handleBulkRestore = () => {
    const filenames = Array.from(selectedFiles);
    bulkStatusChange(filenames, 'ACTIVE');
    setSelectedFiles(new Set());
    setSelectAll(false);
    setBulkActionsAnchorEl(null);
  };

  const handleBulkPermanentDelete = () => {
    const filenames = Array.from(selectedFiles);
    bulkDelete(filenames);
    setSelectedFiles(new Set());
    setSelectAll(false);
    setBulkActionsAnchorEl(null);
  };

  const handleBulkActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setBulkActionsAnchorEl(event.currentTarget);
  };

  const handleBulkActionsClose = () => {
    setBulkActionsAnchorEl(null);
  };

  const handleClearSelection = () => {
    setSelectedFiles(new Set());
    setSelectAll(false);
    setBulkActionsAnchorEl(null);
  };

  const handleSort = (columnId: keyof FileMetadataDTO) => {
    if (sortBy === columnId) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(columnId);
      setSortDirection('asc');
    }
  };

  // Filtrowanie plików w zależności od wybranej zakładki
  const filteredFiles = () => {
    let filtered = files;

    // Filtrowanie według zakładki
    switch (tabValue) {
      case 0: // Aktywne pliki
        filtered = filtered.filter(file => file.status === 'ACTIVE' || !file.status);
        break;
      case 1: // Kosz (pliki odłączone)
        filtered = filtered.filter(file => file.status === 'DETACHED');
        break;
    }

    // Sortowanie
    return [...filtered].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (!aValue || !bValue) return 0;
      if (sortDirection === 'asc') return String(aValue).localeCompare(String(bValue));
      return String(bValue).localeCompare(String(aValue));
    });
  };

  const sortedFiles = filteredFiles();

  const handleDownload = async (file: FileMetadataDTO) => {
    if (!file.filename) {
      enqueueSnackbar('Brak nazwy pliku', { variant: 'error' });
      return;
    }
    await downloadFile(file.filename, file.originalFilename);
  };

  const handleFilePreview = async (file: FileMetadataDTO) => {
    if (!file.filename || !file.contentType) {
      enqueueSnackbar('Brak informacji o pliku', { variant: 'error' });
      return;
    }

    const contentType = file.contentType.toLowerCase();

    // Handle images
    if (contentType.startsWith('image/')) {
      try {
        // Get the download URL from the API
        const response = await MinIoFileStorageService.getApiFilesByFilenameDownload({
          filename: file.filename
        });

        // The response contains downloadUrl
        const downloadUrl = (response as any).downloadUrl;
        if (!downloadUrl) {
          throw new Error('No download URL in response');
        }

        // Fetch the actual image using the download URL
        const imageResponse = await fetch(downloadUrl);
        if (!imageResponse.ok) {
          throw new Error(`HTTP error! status: ${imageResponse.status}`);
        }

        const blob = await imageResponse.blob();
        const imageUrl = URL.createObjectURL(blob);
        setPreviewImageUrl(imageUrl);
        setPreviewFileName(file.originalFilename || file.filename);
        setPreviewModalOpen(true);
      } catch (error) {
        console.error('Error loading image preview:', error);
        enqueueSnackbar('Błąd podczas ładowania podglądu obrazu', { variant: 'error' });
      }
      return;
    }

    // Handle PDF files
    if (contentType.includes('pdf')) {
      try {
        // Get the download URL from the API
        const response = await MinIoFileStorageService.getApiFilesByFilenameDownload({
          filename: file.filename
        });

        // The response contains downloadUrl
        const downloadUrl = (response as any).downloadUrl;
        if (!downloadUrl) {
          throw new Error('No download URL in response');
        }

        // Open the PDF using the download URL directly
        window.open(downloadUrl, '_blank');
      } catch (error) {
        console.error('Error opening PDF:', error);
        enqueueSnackbar('Błąd podczas otwierania pliku PDF', { variant: 'error' });
      }
      return;
    }

    // Handle other file types - show confirmation dialog
    setFileToPreview(file);
    setConfirmPreviewOpen(true);
  };

  const handlePreviewConfirm = () => {
    if (fileToPreview) {
      handleDownload(fileToPreview);
    }
    setConfirmPreviewOpen(false);
    setFileToPreview(null);
  };

  const handleClosePreviewModal = () => {
    setPreviewModalOpen(false);
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
      setPreviewImageUrl(null);
    }
    setPreviewFileName('');
  };

  const handleOpenRelatedObject = (file: FileMetadataDTO) => {
    if (!file.relatedObjectId || !file.relatedObjectType) {
      return;
    }

    const objectId = parseInt(file.relatedObjectId);
    if (isNaN(objectId)) {
      enqueueSnackbar('Nieprawidłowy ID powiązanego obiektu', { variant: 'error' });
      return;
    }

    if (file.relatedObjectType === 'TASK') {
      setSelectedTaskId(objectId);
      setTaskDetailOpen(true);
    } else if (file.relatedObjectType === 'LEAD') {
      setSelectedLeadId(objectId);
      setLeadDetailOpen(true);
    }
  };

  const handleCloseTaskDetail = () => {
    setTaskDetailOpen(false);
    setSelectedTaskId(null);
  };

  const handleCloseLeadDetail = () => {
    setLeadDetailOpen(false);
    setSelectedLeadId(null);
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragOver to false if we're leaving the main container
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Process the first file (similar to current file input behavior)
    const file = files[0];
    setSelectedFile(file);
    setIsFileUploading(true); // START loading

    try {
      await uploadFileWithErrorHandling(file);
      setSelectedFile(null);
      enqueueSnackbar('Plik został pomyślnie przesłany', { variant: 'success' });
    } catch (error: any) {
      console.error('Upload failed:', error);
      enqueueSnackbar(error.message || 'Błąd podczas przesyłania pliku', { variant: 'error' });
    } finally {
      setIsFileUploading(false); // STOP loading
    }
  };


  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setSelectedFile(file);
      setIsFileUploading(true); // START loading

      // Automatically upload the selected file
      try {
        await uploadFileWithErrorHandling(file);
        setSelectedFile(null);
        enqueueSnackbar('Plik został pomyślnie przesłany', { variant: 'success' });
      } catch (error: any) {
        console.error('Upload failed:', error);
        enqueueSnackbar(error.message || 'Błąd podczas przesyłania pliku', { variant: 'error' });
      } finally {
        setIsFileUploading(false); // STOP loading
      }
    }
    // Reset the input value so the same file can be selected again
    event.target.value = '';
  };

  // Upload file with error handling as specified in the issue
  const uploadFileWithErrorHandling = async (file: File) => {
    try {
      await uploadFile({
        formData: { file }
      });
      
      await refreshFiles();
      
      // Refresh storage usage in sidebar
      queryClient.invalidateQueries({ queryKey: ['MinIoFileStorageServiceGetApiFilesStorageInfo'] });
    } catch (error: any) {
      console.error('Upload error details:', error);
      
      // Różne typy błędów
      if (error.message?.includes('413')) {
        throw new Error('Plik jest za duży');
      } else if (error.message?.includes('415')) {
        throw new Error('Nieobsługiwany typ pliku');
      } else {
        throw new Error('Błąd podczas przesyłania pliku');
      }
    }
  };

  const handleAddFileClick = () => {
    const fileInput = document.getElementById('file-upload-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleDeleteConfirm = () => {
    if (fileToDelete) {
      deleteFile(fileToDelete);
      setConfirmDeleteOpen(false);
      setFileToDelete(null);
    }
  };

  const formatFileSize = (bytes?: number): string => {
    if (bytes === undefined) return 'Nieznany';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'Nieznana';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pl-PL');
    } catch {
      return dateString;
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Accessibility helper for tabs
  const a11yProps = (index: number) => {
    return {
      id: `files-tab-${index}`,
      'aria-controls': `files-tabpanel-${index}`,
    };
  };

  // Column configuration functions
  const handleColumnConfigSave = (configs: ColumnConfig[]) => {
    // Save configuration to localStorage
    localStorage.setItem('files-columns-config', JSON.stringify(configs));
    
    // Update visible columns based on configuration
    const visibleColumns = configs
      .filter(config => config.visible)
      .map(config => {
        const originalColumn = defaultColumns.find(col => col.id.toString() === config.id);
        return originalColumn ? {
          ...originalColumn,
          label: config.label
        } : null;
      })
      .filter(Boolean) as FileColumn[];
    
    setColumns(visibleColumns);
    enqueueSnackbar('Konfiguracja kolumn została zapisana', { variant: 'success' });
  };

  const getColumnConfigs = (): ColumnConfig[] => {
    const savedConfig = localStorage.getItem('files-columns-config');
    if (savedConfig) {
      try {
        const parsedConfig: ColumnConfig[] = JSON.parse(savedConfig);
        if (Array.isArray(parsedConfig)) {
          // Ensure userId column is always visible
          const updatedConfig = parsedConfig.map(col => 
            col.id === 'userId' ? { ...col, visible: true } : col
          );
          return updatedConfig;
        }
      } catch (error) {
        console.error('Błąd podczas parsowania zapisanych kolumn:', error);
      }
    }
    
    // Return default configuration
    return defaultColumns.map(col => ({
      id: col.id.toString(),
      label: col.label,
      visible: true,
      minWidth: col.minWidth,
      align: col.align
    }));
  };

  // Initialize columns from localStorage
  useEffect(() => {
    const savedConfig = localStorage.getItem('files-columns-config');
    if (savedConfig) {
      try {
        const parsedConfig: ColumnConfig[] = JSON.parse(savedConfig);
        if (Array.isArray(parsedConfig)) {
          // Ensure userId column is always visible
          const updatedConfig = parsedConfig.map(col => 
            col.id === 'userId' ? { ...col, visible: true } : col
          );
          
          // Convert ColumnConfig back to FileColumn format and filter visible columns
          const visibleColumns = updatedConfig
            .filter(config => config.visible)
            .map(config => {
              const originalColumn = defaultColumns.find(col => col.id.toString() === config.id);
              return originalColumn ? {
                ...originalColumn,
                label: config.label
              } : null;
            })
            .filter(Boolean) as FileColumn[];
          
          if (visibleColumns.length > 0) {
            setColumns(visibleColumns);
          }
        }
      } catch (error) {
        console.error('Błąd podczas parsowania zapisanych kolumn:', error);
      }
    }
  }, []);

  // Hash-based navigation and file highlighting
  useEffect(() => {
    const hash = location.hash || window.location.hash;
    if (hash && files.length > 0) {
      // Add small delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        const fileId = hash.substring(1);
        // Find file row by data attribute or ID
        const fileRow = document.querySelector(`[data-file-id="${fileId}"]`);
        if (fileRow) {
          fileRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          fileRow.classList.add('highlighted');
          setTimeout(() => {
            fileRow.classList.remove('highlighted');
          }, 2500);
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [location.hash, files]);

  // Add CSS styles for file highlighting
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .highlighted {
        background-color: #fff3cd !important;
        border: 2px solid #ffc107 !important;
        animation: highlightPulse 2.5s ease-in-out;
      }
      
      @keyframes highlightPulse {
        0% { background-color: #fff3cd; }
        50% { background-color: #ffeaa7; }
        100% { background-color: #fff3cd; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
      <Box 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          mb: 3,
          position: 'relative'
        }}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Drag and Drop Overlay */}
        {isDragOver && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
              border: '3px dashed #2196f3',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              backdropFilter: 'blur(2px)',
            }}
          >
            <CloudUploadIcon 
              sx={{ 
                fontSize: '4rem', 
                color: '#2196f3',
                mb: 2,
                '@keyframes bounce': {
                  '0%, 20%, 50%, 80%, 100%': {
                    transform: 'translateY(0)',
                  },
                  '40%': {
                    transform: 'translateY(-10px)',
                  },
                  '60%': {
                    transform: 'translateY(-5px)',
                  },
                },
                animation: 'bounce 1s infinite'
              }} 
            />
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#2196f3', 
                fontWeight: 600,
                textAlign: 'center',
                mb: 1
              }}
            >
              Upuść nowy plik tutaj
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#666', 
                textAlign: 'center'
              }}
            >
              Plik zostanie automatycznie dodany do listy
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <input
            accept="*/*"
            style={{ display: 'none' }}
            id="file-upload-input"
            type="file"
            onChange={handleFileSelect}
          />
          <Button
              startIcon={<CloudUploadIcon />}
              onClick={handleAddFileClick}
              disabled={isUploading || isFileUploading} // Dodaj isFileUploading
              sx={{
                background: 'linear-gradient(to right, #06b6d4, #2563eb)',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: '9999px',
                fontWeight: 400,
                textTransform: 'none',
                boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  background: 'linear-gradient(to right, #059dc5, #1e4ed8)',
                },
                '&:disabled': {
                  background: '#9ca3af',
                  color: '#ffffff',
                },
              }}
          >
            {(isUploading || isFileUploading) ? 'Przesyłanie...' : 'Dodaj plik'}
          </Button>
        </Box>

        <Paper
            sx={{
              width: '100%',
              overflow: 'hidden',
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              bgcolor: '#ffffff',
            }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="pliki zakładki"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                label={<Box sx={{ fontSize:'0.8em', display: 'flex', alignItems: 'center' }}>
                  <FolderIcon sx={{ mr: 1 }} />
                  Aktywne
                  {files.filter(f => f.status === 'ACTIVE' || !f.status).length > 0 && (
                    <Chip
                      size="small"
                      label={files.filter(f => f.status === 'ACTIVE' || !f.status).length}
                      sx={{ 
                        ml: 1, 
                        pointerEvents: 'none',
                        bgcolor: '#4caf50',
                        color: 'white'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </Box>}
                {...a11yProps(0)}
              />
              <Tab
                label={<Box sx={{ fontSize:'0.8em', display: 'flex', alignItems: 'center' }}>
                  <TrashIcon sx={{ mr: 1 }} />
                  Kosz
                  {files.filter(f => f.status === 'DETACHED').length > 0 && (
                    <Chip
                      size="small"
                      label={files.filter(f => f.status === 'DETACHED').length}
                      sx={{ 
                        ml: 1, 
                        pointerEvents: 'none',
                        bgcolor: '#f44336',
                        color: 'white'
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </Box>}
                {...a11yProps(1)}
              />
            </Tabs>
          </Box>

          {/* Notification for trash tab about 30-day deletion policy */}
          {tabValue === 1 && (
              <Alert severity="info" sx={{ m: 2 }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                  <strong>Informacja:</strong> Pliki w koszu są automatycznie usuwane po 30 dniach od momentu przeniesienia do kosza.
                </Typography>
              </Alert>
          )}

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            px: 2,
            py: 0
          }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => setColumnConfigOpen(true)}
              sx={{
                fontSize: '0.7rem',
                minWidth: 'auto',
                px: 2,
                borderRadius: '9999px'
              }}
            >
              Kolumny
            </Button>
            <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={sortedFiles.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Wierszy na stronę:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} z ${count}`}
                sx={{
                  '.MuiTablePagination-toolbar': {
                    backgroundColor: 'transparent',
                    fontSize: '0.8rem',
                    border: 'none',
                  },
                  '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                    fontSize: '0.8rem',
                  },
                }}
            />
          </Box>

          <TableContainer sx={{ 
            maxHeight: isMobile ? 'auto' : 'calc(100vh - 250px)',
            overflowX: 'auto'
          }}>
            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
            ) : (
                <Table stickyHeader sx={{ minWidth: isMobile ? 650 : 'auto' }}>
                  <TableHead>
                    <TableRow sx={{ height: isMobile ? 40 : 48 }}>
                      {columns.map((column) => (
                          <TableCell
                              key={column.id}
                              align={column.align}
                              sx={{
                                minWidth: isMobile ? Math.max(column.minWidth! * 0.8, 50) : column.minWidth,
                                backgroundColor: '#ffffff',
                                color: '#1f2937',
                                fontWeight: 600,
                                fontSize: isMobile ? '0.7rem' : '0.8rem',
                                borderBottom: '1px solid #e5e7eb',
                                cursor: column.id !== 'actions' && column.id !== 'thumbnail' && column.id !== 'select' ? 'pointer' : 'default',
                                px: isMobile ? 1 : 2,
                                py: isMobile ? 0.5 : 1,
                              }}
                              onClick={() =>
                                  column.id !== 'actions' && column.id !== 'thumbnail' && column.id !== 'select' && handleSort(column.id as keyof FileMetadataDTO)
                              }
                          >
                            {column.id === 'select' ? (
                              selectedFiles.size > 0 ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      fontWeight: 600,
                                      color: '#1976d2',
                                      fontSize: isMobile ? '0.65rem' : '0.75rem',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    zaznaczono: {selectedFiles.size}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={handleBulkActionsClick}
                                    sx={{ 
                                      p: 0.25,
                                      color: '#1976d2',
                                      '&:hover': {
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                      }
                                    }}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Checkbox
                                  checked={selectAll}
                                  onChange={handleSelectAll}
                                  size={isMobile ? "small" : "medium"}
                                  sx={{ p: 0 }}
                                />
                              )
                            ) : (
                              <>
                                {isMobile && column.label.length > 8 ? 
                                  column.label.substring(0, 8) + '...' : 
                                  column.label
                                }
                                {sortBy === column.id && (
                                    <Box component="span" ml={0.5}>
                                      {sortDirection === 'asc' ? '↑' : '↓'}
                                    </Box>
                                )}
                              </>
                            )}
                          </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedFiles.length > 0 ? (
                        sortedFiles
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((file, index) => (
                                <TableRow
                                    key={file.filename || index}
                                    data-file-id={file.id}
                                    hover
                                    onClick={() => handleFilePreview(file)}
                                    sx={{
                                      backgroundColor: selectedFiles.has(file.filename || '') 
                                        ? '#e3f2fd' 
                                        : index % 2 === 0 ? '#ffffff' : '#f9fafb',
                                      transition: 'all 0.2s ease-in-out',
                                      '&:hover': {
                                        backgroundColor: selectedFiles.has(file.filename || '') 
                                          ? '#bbdefb' 
                                          : '#e0f2fe',
                                      },
                                      height: isMobile ? '32px' : '36px',
                                      '& td': {
                                        py: isMobile ? '2px' : '4px',
                                        px: isMobile ? 1 : 2,
                                        fontSize: isMobile ? '0.75rem' : '0.875rem',
                                      },
                                      cursor: 'pointer'
                                    }}
                                >
                                  {columns.map((column) => {
                                    // Render cell based on column type
                                    if (column.id === 'select') {
                                      return (
                                        <TableCell key={column.id} align="center" onClick={(e) => e.stopPropagation()}>
                                          <Checkbox
                                            checked={selectedFiles.has(file.filename || '')}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              if (file.filename) {
                                                handleSelectFile(file.filename);
                                              }
                                            }}
                                            size={isMobile ? "small" : "medium"}
                                            sx={{ p: 0 }}
                                          />
                                        </TableCell>
                                      );
                                    }
                                    if (column.id === 'thumbnail') {
                                      return (
                                        <TableCell key={column.id} align="center">
                                          {file.filename && (
                                            <LazyThumbnail 
                                              filename={file.filename} 
                                              size={isMobile ? 50 : 150}
                                              quality={100}
                                            />
                                          )}
                                        </TableCell>
                                      );
                                    }
                                    if (column.id === 'originalFilename') {
                                      return (
                                        <TableCell 
                                          key={column.id}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleFilePreview(file);
                                          }}
                                          sx={{ 
                                            maxWidth: isMobile ? '120px' : '200px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            cursor: 'pointer',
                                            color: '#1976d2',
                                            '&:hover': {
                                              textDecoration: 'underline',
                                              backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                            }
                                          }}
                                        >
                                          {file.originalFilename || file.filename}
                                        </TableCell>
                                      );
                                    }
                                    if (column.id === 'contentType') {
                                      return (
                                        <TableCell key={column.id} align="center" onClick={(e) => e.stopPropagation()}>
                                          <FileTypeIcon contentType={file.contentType} size="small" />
                                        </TableCell>
                                      );
                                    }
                                    if (column.id === 'fileSize') {
                                      return (
                                        <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                          {formatFileSize(file.fileSize)}
                                        </TableCell>
                                      );
                                    }
                                    if (column.id === 'userId') {
                                      // const cachedAvatar = file.userId ? cachedAvatars.get(file.userId) : null;
                                      // const hasAvatarImage = cachedAvatar?.avatarUrl;
                                      
                                      return (
                                        <TableCell key={column.id} align="center" onClick={(e) => e.stopPropagation()}>
                                          <Tooltip title={getUserDisplayName(file.userId)}>
                                            {/* <Avatar
                                              src={hasAvatarImage ? cachedAvatar.avatarUrl || undefined : undefined}
                                              sx={{
                                                width: 28,
                                                height: 28,
                                                bgcolor: file.userId ? 'primary.main' : 'grey.300',
                                                fontSize: '0.75rem',
                                                border: hasAvatarImage ? '1px solid' : 'none',
                                                borderColor: hasAvatarImage ? 'primary.main' : 'transparent',
                                                cursor: 'pointer',
                                              }}
                                            >
                                              {!hasAvatarImage && getUserInitials(file.userId)}
                                            </Avatar> */}
                                            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                              {getUserInitials(file.userId)}
                                            </Typography>
                                          </Tooltip>
                                        </TableCell>
                                      );
                                    }
                                    if (column.id === 'createdAt') {
                                      return (
                                        <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                          {formatDate(file.createdAt)}
                                        </TableCell>
                                      );
                                    }
                                    if (column.id === 'actions') {
                                      return (
                                        <TableCell key={column.id} align="right">
                                          <Box 
                                            sx={{ 
                                              display: 'flex', 
                                              alignItems: 'center', 
                                              justifyContent: 'flex-end',
                                              gap: 0.5,
                                              minWidth: 'fit-content'
                                            }}
                                          >
                                            <Tooltip title="Pobierz">
                                              <IconButton
                                                color="primary" 
                                                size="small"
                                                sx={{ p: isMobile ? 0.5 : 1 }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDownload(file);
                                                }}
                                              >
                                                <DownloadIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            {tabValue === 1 && file.status === 'DETACHED' && (
                                              <Tooltip title="Przywróć z kosza">
                                                <IconButton
                                                  color="success"
                                                  size="small"
                                                  sx={{ p: isMobile ? 0.5 : 1 }}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    restoreFile(file);
                                                  }}
                                                >
                                                  <RestoreIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                            <Tooltip title="Usuń">
                                              <IconButton
                                                color="error"
                                                size="small"
                                                sx={{ p: isMobile ? 0.5 : 1 }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setFileToDelete(file);
                                                  setConfirmDeleteOpen(true);
                                                }}
                                              >
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            {file.relatedObjectId && file.relatedObjectType && (
                                              <Tooltip title={
                                                file.relatedObjectType === 'TASK' 
                                                  ? `Przejdź do zadania #${file.relatedObjectId}` 
                                                  : file.relatedObjectType === 'LEAD'
                                                  ? `Przejdź do leada #${file.relatedObjectId}`
                                                  : `Powiązany obiekt #${file.relatedObjectId}`
                                              }>
                                                <IconButton
                                                  color="info"
                                                  size="small"
                                                  sx={{ p: isMobile ? 0.5 : 1 }}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenRelatedObject(file);
                                                  }}
                                                >
                                                  {file.relatedObjectType === 'TASK' ? (
                                                    <TaskIcon fontSize="small" />
                                                  ) : file.relatedObjectType === 'LEAD' ? (
                                                    <LeadIcon fontSize="small" />
                                                  ) : (
                                                    <LinkIcon fontSize="small" />
                                                  )}
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                          </Box>
                                        </TableCell>
                                      );
                                    }
                                    return null;
                                  })}
                                </TableRow>
                            ))
                    ) : (
                        <TableRow>
                          <TableCell colSpan={columns.length} align="center">
                            <Box sx={{ py: 5, opacity: 0.6 }}>
                              <CloudUploadIcon fontSize="large" />
                              <Typography variant="h6" mt={1} fontWeight={500}>
                                Brak plików do wyświetlenia
                              </Typography>
                              <Typography variant="body2">
                                Kliknij „Dodaj plik”, aby przesłać pierwszy dokument.
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                    )}
                  </TableBody>
                </Table>
            )}
          </TableContainer>
          <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={sortedFiles.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Wierszy na stronę:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} z ${count}`}
              sx={{
                '.MuiTablePagination-toolbar': {
                  backgroundColor: '#f9fafb',
                  fontSize: '0.8rem',
                  borderTop: '1px solid #e5e7eb',
                },
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  fontSize: '0.8rem',
                },
              }}
          />
        </Paper>

        {/* Modern Bulk Actions Dropdown Menu */}
        <Menu
          anchorEl={bulkActionsAnchorEl}
          open={Boolean(bulkActionsAnchorEl)}
          onClose={handleBulkActionsClose}
          PaperProps={{
            elevation: 8,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
              mt: 1.5,
              minWidth: 200,
              borderRadius: '12px',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Akcje dla {selectedFiles.size} {selectedFiles.size === 1 ? 'pliku' : 'plików'}
            </Typography>
          </Box>

          {tabValue !== 1 && (
            <MenuItem 
              onClick={handleBulkMoveToTrash}
              disabled={isBulkChangingStatus}
              sx={{ 
                py: 1.5, 
                px: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255, 152, 0, 0.08)'
                }
              }}
            >
              <ListItemIcon sx={{ color: '#ff9800' }}>
                <TrashIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={isBulkChangingStatus ? 'Przenoszenie...' : 'Przenieś do kosza'}
                primaryTypographyProps={{ fontSize: '0.875rem' }}
              />
            </MenuItem>
          )}

          {tabValue === 1 && (
            <>
              <MenuItem 
                onClick={handleBulkRestore}
                disabled={isBulkChangingStatus}
                sx={{ 
                  py: 1.5, 
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(76, 175, 80, 0.08)'
                  }
                }}
              >
                <ListItemIcon sx={{ color: '#4caf50' }}>
                  <RestoreIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={isBulkChangingStatus ? 'Przywracanie...' : 'Przywróć'}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </MenuItem>
              <MenuItem 
                onClick={handleBulkPermanentDelete}
                disabled={isBulkDeleting}
                sx={{ 
                  py: 1.5, 
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.08)'
                  }
                }}
              >
                <ListItemIcon sx={{ color: '#f44336' }}>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={isBulkDeleting ? 'Usuwanie...' : 'Usuń trwale'}
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </MenuItem>
            </>
          )}

          <Divider sx={{ my: 0.5 }} />

          <MenuItem 
            onClick={handleClearSelection}
            sx={{ 
              py: 1.5, 
              px: 2,
              '&:hover': {
                backgroundColor: 'rgba(158, 158, 158, 0.08)'
              }
            }}
          >
            <ListItemIcon sx={{ color: '#9e9e9e' }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </Box>
            </ListItemIcon>
            <ListItemText 
              primary="Anuluj zaznaczenie"
              primaryTypographyProps={{ fontSize: '0.875rem' }}
            />
          </MenuItem>
        </Menu>

        <DeleteConfirmDialog
            open={confirmDeleteOpen}
            onClose={() => setConfirmDeleteOpen(false)}
            onConfirm={handleDeleteConfirm}
            title={
              fileToDelete?.status === 'DETACHED' 
                ? "Potwierdź trwałe usunięcie" 
                : "Potwierdź przeniesienie do kosza"
            }
            content={
              fileToDelete?.status === 'DETACHED'
                ? `Czy na pewno chcesz trwale usunąć plik: ${fileToDelete?.originalFilename || fileToDelete?.filename}? Ta operacja jest nieodwracalna.`
                : `Czy na pewno chcesz przenieść plik do kosza: ${fileToDelete?.originalFilename || fileToDelete?.filename}? Będzie można go później przywrócić lub trwale usunąć.`
            }
            isSubmitting={isDeleting || isChangingStatus}
        />

        {/* Image Preview Modal */}
        <Modal
          open={previewModalOpen}
          onClose={handleClosePreviewModal}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{
            timeout: 500,
          }}
        >
          <Fade in={previewModalOpen}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '98vw',
                height: '98vh',
                bgcolor: 'background.paper',
                border: '2px solid #000',
                boxShadow: 24,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                width: '100%',
                mb: 2 
              }}>
                <Typography variant="h6" component="h2">
                  {previewFileName}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    onClick={() => {
                      const fileToDownload = files.find(f => f.originalFilename === previewFileName || f.filename === previewFileName);
                      if (fileToDownload) {
                        handleDownload(fileToDownload);
                      }
                    }}
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                  >
                    Pobierz
                  </Button>
                  <Button
                    onClick={handleClosePreviewModal}
                    size="small"
                    variant="contained"
                  >
                    Zamknij
                  </Button>
                </Box>
              </Box>
              {previewImageUrl && (
                <Box
                  component="img"
                  src={previewImageUrl}
                  alt={previewFileName}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 'calc(98vh - 100px)',
                    objectFit: 'contain',
                    width: 'auto',
                    height: 'auto',
                  }}
                />
              )}
            </Box>
          </Fade>
        </Modal>

        {/* Confirmation Dialog for Unsupported Files */}
        <Dialog
          open={confirmPreviewOpen}
          onClose={() => setConfirmPreviewOpen(false)}
        >
          <DialogTitle>Podgląd niedostępny</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Szybki podgląd nie możliwy, chcesz pobrać plik?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmPreviewOpen(false)}>
              Anuluj
            </Button>
            <Button onClick={handlePreviewConfirm} variant="contained">
              Pobierz
            </Button>
          </DialogActions>
        </Dialog>

        {/* Task Detail Dialog */}
        {selectedTaskId && (
          <TaskDetailDialog
            open={taskDetailOpen}
            onClose={handleCloseTaskDetail}
            taskId={selectedTaskId}
          />
        )}

        {/* Lead Detail Dialog */}
        {selectedLeadId && (
          <LeadDetailDialog
            open={leadDetailOpen}
            onClose={handleCloseLeadDetail}
            leadId={selectedLeadId}
          />
        )}

        {/* Column Configuration Dialog */}
        <ColumnConfigDialog
          open={columnConfigOpen}
          onClose={() => setColumnConfigOpen(false)}
          columns={getColumnConfigs()}
          onSave={handleColumnConfigSave}
          title="Konfiguracja kolumn plików"
        />
      </Box>
  );
};

export default FilesPage;
